import { useState, useCallback, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import { scoreMarketsFiltersAtom, scoreMarketsQueryAtom } from 'r/atoms';
import PaginationControls from '../AnalysisManagerTab/PaginationControls';
import ScoreMarketCard from './ScoreMarketCard';
import { Input } from '@/components/ui/input';

export default function ScoreMarketsTab() {
  const [filters, setFilters] = useAtom(scoreMarketsFiltersAtom);
  const mainQuery = useAtomValue(scoreMarketsQueryAtom);

  const markets = mainQuery.data?.data || [];
  const total = mainQuery.data?.total || 0;
  const isPending = mainQuery.isLoading;
  const isFetching = mainQuery.isFetching;

  const [expandedTextId, setExpandedTextId] = useState<string | null>(null);
  const [localSearch, setLocalSearch] = useState(filters.searchQuery);

  const totalPages = Math.ceil(total / filters.pageSize);

  // Search debounce
  const debouncedSetSearch = useCallback(
    debounce((searchValue: string) => {
      setFilters((prev) => ({
        ...prev,
        searchQuery: searchValue,
        currentPage: 1,
      }));
    }, 750),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    debouncedSetSearch(value);
  };

  const handleSearchClear = () => {
    debouncedSetSearch.cancel();
    setLocalSearch('');
    setFilters((prev) => ({ ...prev, searchQuery: '', currentPage: 1 }));
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setFilters((prev) => ({ ...prev, currentPage: page }));
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newCurrentPage = Math.ceil(
      ((filters.currentPage - 1) * filters.pageSize + 1) / newPageSize,
    );
    setFilters((prev) => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: newCurrentPage,
    }));
  };

  return (
    <div className="w-full bg-background py-6 font-sans">
      <div className="max-w-7xl mx-auto flex flex-col gap-8">
        <div className="bg-background rounded-2xl p-6 shadow-sm border border-bg-accent-tabs">
          {/* Search Bar */}
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
            <h2 className="text-[20px] text-main font-medium">Score markets</h2>

            <div className="relative w-full sm:w-64">
              <Input
                aria-label="Search by KOL, asset or text"
                placeholder="Search by KOL, asset or text..."
                value={localSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full"
              />
              {localSearch && (
                <button
                  onClick={handleSearchClear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Results Summary */}
          <div className="text-sm text-secondary mb-6 font-medium">
            {filters.searchQuery ?
              `Found ${total} result${total !== 1 ? 's' : ''}`
            : `Total: ${total} markets`}
            {isFetching && (
              <span className="ml-2 text-blue-500 animate-pulse">
                Updating...
              </span>
            )}
          </div>

          {isPending && markets.length === 0 ?
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-btn-secondary-normal mb-3"></div>
              <p className="text-secondary">Loading markets...</p>
            </div>
          : <>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {markets.map((market) => (
                  <ScoreMarketCard
                    key={market.id}
                    market={market}
                    expandedTextId={expandedTextId}
                    onToggleExpand={setExpandedTextId}
                  />
                ))}
              </div>

              {markets.length === 0 && (
                <div className="min-h-85 flex items-center justify-center text-center text-secondary py-16 border-2 border-dashed border-bg-accent-tabs rounded-xl">
                  <p className="text-lg font-medium text-main">
                    {filters.searchQuery ?
                      'No results found'
                    : 'No markets yet'}
                  </p>
                </div>
              )}

              <PaginationControls
                page={filters.currentPage - 1}
                total={total}
                limit={filters.pageSize}
                onPageChange={(p) => handlePageChange(p + 1)}
                onLimitChange={handlePageSizeChange}
              />
            </>
          }
        </div>
      </div>
    </div>
  );
}
